import { all, takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import queryString from 'query-string';
import { getRiskConnectedPolicies } from '../risk/slice';
import { getConnectedRisks } from '../policy/slice';
import renderNotification from '../../helpers/notifications/renderNotification';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { onGetRisks } from '../risks/api';
import { selectRisksActiveFilters } from '../filters/selectors';
import { selectRisk } from '../risk/selectors';
import { selectPolicy } from '../policy/selectors';
import {
  createPoliciesToRisks,
  createPoliciesToRisksSucceeded,
  createPoliciesToRisksFailed,
  getPoliciesToRisks,
  getPoliciesToRisksSucceeded,
  getPoliciesToRisksFailed,
  deletePoliciesToRisks,
  deletePoliciesToRisksSucceeded,
  deletePoliciesToRisksFailed,
  bulkAddPoliciesToRisks,
  bulkAddPoliciesToRisksSucceeded,
  bulkAddPoliciesToRisksFailed,
  bulkRemovePoliciesFromRisks,
  bulkRemovePoliciesFromRisksSucceeded,
  bulkRemovePoliciesFromRisksFailed
} from './slice';
import { onGetPoliciesToRisks, onCreatePoliciesToRisks, onDeletePoliciesToRisks } from './api';

const {
  policy_to_risk_create_success,
  policies_to_risk_create_success,
  risk_to_policy_create_success,
  risks_to_policy_create_success,
  policies_to_risk_delete_success,
  policy_to_risk_delete_success,
  risk_to_policy_delete_success
} = NOTIFICATION_MESSAGES;

export function* createPoliciesToRisksWorker({ payload }) {
  const { context, policiesToRisks, showNotification, callback } = payload;
  const dataArray = Array.isArray(policiesToRisks) ? policiesToRisks : [policiesToRisks];

  try {
    yield call(onCreatePoliciesToRisks, dataArray);
    yield put(createPoliciesToRisksSucceeded());
    callback?.();
    if (showNotification) {
      const isPlural = dataArray?.length > 1;
      let message;

      if (context === 'risk') {
        message = isPlural ? policies_to_risk_create_success : policy_to_risk_create_success;
      } else {
        message = isPlural ? risks_to_policy_create_success : risk_to_policy_create_success;
      }

      renderNotification({ type: 'success', message });
    }
  } catch {
    yield put(createPoliciesToRisksFailed());
  }
}

export function* deletePoliciesToRisksWorker({ payload }) {
  const { context, policiesToRisksIds, callback } = payload;
  const idsArray = Array.isArray(policiesToRisksIds) ? policiesToRisksIds : [policiesToRisksIds];

  try {
    yield call(onDeletePoliciesToRisks, idsArray);
    yield put(deletePoliciesToRisksSucceeded());
    callback?.();

    const message = context === 'risk' ? policy_to_risk_delete_success : risk_to_policy_delete_success;

    renderNotification({ type: 'success', message });
  } catch {
    yield put(deletePoliciesToRisksFailed());
  }
}

export function* getPoliciesToRisksWorker({ payload }) {
  const { policy_id, risk_id } = payload;

  try {
    const response = yield call(onGetPoliciesToRisks, { policy_id, risk_id, paging: false });
    yield put(getPoliciesToRisksSucceeded(response.data));
  } catch {
    yield put(getPoliciesToRisksFailed());
  }
}

export function* reloadPoliciesToRisksWorker() {
  const risk = yield select(selectRisk);
  const policy = yield select(selectPolicy);
  const item = risk || policy;

  if (!item) return;

  const { id } = item;

  if (risk) {
    yield put(getPoliciesToRisks({ risk_id: [id] }));
    yield put(getRiskConnectedPolicies({ riskId: id }));
    return;
  }

  yield put(getPoliciesToRisks({ policy_id: [id] }));
  yield put(getConnectedRisks({ policyId: id }));
}

export function* bulkAddPoliciesToRisksWorker(action) {
  const { policyIds, callback } = action.payload;

  try {
    const riskFilters = yield select(selectRisksActiveFilters);
    const params = queryString.parse(riskFilters);
    params.paging = false;

    const risksResponse = yield call(onGetRisks, params);
    const riskIds = risksResponse.data.map((risk) => risk.id);

    const policiesToRisksResponse = yield call(onGetPoliciesToRisks, {
      policy_id: policyIds,
      paging: false
    });

    const existingPairs = new Set(policiesToRisksResponse.data.map((p2r) => `${p2r.risk_id}-${p2r.policy_id}`));

    const newPairs = [];
    riskIds.forEach((riskId) => {
      policyIds.forEach((policyId) => {
        if (!existingPairs.has(`${riskId}-${policyId}`)) {
          newPairs.push({ risk_id: riskId, policy_id: policyId });
        }
      });
    });

    if (newPairs.length > 0) {
      yield call(onCreatePoliciesToRisks, newPairs);
    }

    yield put(bulkAddPoliciesToRisksSucceeded());
    callback?.();

    const message = policyIds.length === 1 ? policy_to_risk_create_success : policies_to_risk_create_success;
    renderNotification({ type: 'success', message });
  } catch {
    yield put(bulkAddPoliciesToRisksFailed());
  }
}

export function* bulkRemovePoliciesFromRisksWorker(action) {
  const { policyIds, callback } = action.payload;

  try {
    const filters = yield select(selectRisksActiveFilters);
    const params = queryString.parse(filters);
    params.paging = false;

    const risksResponse = yield call(onGetRisks, params);
    const riskIds = risksResponse.data.map((risk) => risk.id);

    const policiesToRisksResponse = yield call(onGetPoliciesToRisks, {
      policy_id: policyIds,
      paging: false
    });

    const riskIdSet = new Set(riskIds);
    const idsToDelete = policiesToRisksResponse.data
      .filter((p2r) => riskIdSet.has(p2r.risk_id) && policyIds.includes(p2r.policy_id))
      .map((p2r) => p2r.id);

    if (idsToDelete.length > 0) {
      yield call(onDeletePoliciesToRisks, idsToDelete);
    }

    yield put(bulkRemovePoliciesFromRisksSucceeded());
    callback?.();

    const message = policyIds.length === 1 ? policy_to_risk_delete_success : policies_to_risk_delete_success;
    renderNotification({ type: 'success', message });
  } catch {
    yield put(bulkRemovePoliciesFromRisksFailed());
  }
}

export default function* policiesToRisksSaga() {
  yield all([
    takeLatest(createPoliciesToRisks.type, createPoliciesToRisksWorker),
    takeEvery(createPoliciesToRisksSucceeded.type, reloadPoliciesToRisksWorker),
    takeLatest(deletePoliciesToRisks.type, deletePoliciesToRisksWorker),
    takeEvery(deletePoliciesToRisksSucceeded.type, reloadPoliciesToRisksWorker),
    takeLatest(getPoliciesToRisks.type, getPoliciesToRisksWorker),
    takeEvery(bulkAddPoliciesToRisks.type, bulkAddPoliciesToRisksWorker),
    takeEvery(bulkRemovePoliciesFromRisks.type, bulkRemovePoliciesFromRisksWorker)
  ]);
}
